import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { providersService } from '../services/providers.service'
import { ServiceProvider, ServiceType } from '../types'
import toast from 'react-hot-toast'

export function useProviders(params?: {
  serviceType?: ServiceType
  city?: string
  page?: number
  limit?: number
}) {
  return useQuery({
    queryKey: ['providers', params],
    queryFn: () => providersService.getProviders(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useProvider(id: string) {
  return useQuery({
    queryKey: ['provider', id],
    queryFn: () => providersService.getProviderById(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

export function useAmbulanceProviders(params?: {
  lat?: number
  lng?: number
  radius?: number
}) {
  return useQuery({
    queryKey: ['ambulance-providers', params],
    queryFn: () => providersService.getAmbulanceProviders(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useSearchProviders() {
  return useMutation({
    mutationFn: (params: { query: string; serviceType?: ServiceType }) =>
      providersService.searchProviders(params.query, params.serviceType),
  })
}

export function useCreateProviderProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (providerData: any) => providersService.createProviderProfile(providerData),
    onSuccess: (newProvider) => {
      toast.success('Provider profile created successfully')
      queryClient.invalidateQueries({ queryKey: ['providers'] })
      queryClient.setQueryData(['provider', newProvider.id], newProvider)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create provider profile'
      toast.error(message)
    },
  })
}

export function useUpdateProviderProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      providersService.updateProviderProfile(id, data),
    onSuccess: (updatedProvider) => {
      toast.success('Provider profile updated successfully')
      queryClient.invalidateQueries({ queryKey: ['providers'] })
      queryClient.setQueryData(['provider', updatedProvider.id], updatedProvider)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update provider profile'
      toast.error(message)
    },
  })
}

export function useProviderAvailability() {
  return useMutation({
    mutationFn: (params: { providerId: string; date: string }) =>
      providersService.getProviderAvailability(params.providerId, params.date),
  })
}

// Admin-specific hooks
export function useAdminProviderStats() {
  return useQuery({
    queryKey: ['admin-provider-stats'],
    queryFn: () => providersService.getAdminProviderStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useRecentProviders(limit: number = 5) {
  return useQuery({
    queryKey: ['recent-providers', limit],
    queryFn: () => providersService.getRecentProviders(limit),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

export function usePendingProviders() {
  return useQuery({
    queryKey: ['pending-providers'],
    queryFn: () => providersService.getPendingProviders(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useActiveProviders(params?: {
  page?: number
  limit?: number
  serviceType?: string
  city?: string
  search?: string
}) {
  return useQuery({
    queryKey: ['active-providers', params],
    queryFn: () => providersService.getActiveProviders(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useApproveProvider() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (providerId: string) => providersService.approveProvider(providerId),
    onSuccess: () => {
      toast.success('Provider approved successfully')
      queryClient.invalidateQueries({ queryKey: ['pending-providers'] })
      queryClient.invalidateQueries({ queryKey: ['active-providers'] })
      queryClient.invalidateQueries({ queryKey: ['admin-provider-stats'] })
      queryClient.invalidateQueries({ queryKey: ['recent-providers'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to approve provider'
      toast.error(message)
    },
  })
}

export function useRejectProvider() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ providerId, reason }: { providerId: string; reason: string }) =>
      providersService.rejectProvider(providerId, reason),
    onSuccess: () => {
      toast.success('Provider application rejected')
      queryClient.invalidateQueries({ queryKey: ['pending-providers'] })
      queryClient.invalidateQueries({ queryKey: ['admin-provider-stats'] })
      queryClient.invalidateQueries({ queryKey: ['recent-providers'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to reject provider'
      toast.error(message)
    },
  })
}

export function useDeactivateProvider() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ providerId, reason }: { providerId: string; reason: string }) =>
      providersService.deactivateProviderWithReason(providerId, reason),
    onSuccess: () => {
      toast.success('Provider deactivated successfully')
      queryClient.invalidateQueries({ queryKey: ['active-providers'] })
      queryClient.invalidateQueries({ queryKey: ['admin-provider-stats'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to deactivate provider'
      toast.error(message)
    },
  })
}

export function useProviderAnalytics(period?: string, serviceType?: string) {
  return useQuery({
    queryKey: ['provider-analytics', period, serviceType],
    queryFn: () => providersService.getProviderAnalytics(period, serviceType),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}